import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentRotationsTable = ({ rotations }) => {
  const navigate = useNavigate();

  const getStatusConfig = (status) => {
    const configs = {
      verified: {
        label: 'Verified',
        bgClass: 'bg-success/20',
        textClass: 'text-success',
        borderClass: 'border-success/40',
        icon: 'CheckCircle2'
      },
      pending: {
        label: 'Pending',
        bgClass: 'bg-accent-blue/20',
        textClass: 'text-accent-blue',
        borderClass: 'border-accent-blue/40',
        icon: 'Clock'
      },
      violation: {
        label: 'Violation',
        bgClass: 'bg-error/20',
        textClass: 'text-error',
        borderClass: 'border-error/40',
        icon: 'AlertTriangle',
        pulse: true
      },
      discrepancy: {
        label: 'Discrepancy',
        bgClass: 'bg-warning/20',
        textClass: 'text-warning',
        borderClass: 'border-warning/40',
        icon: 'AlertCircle'
      }
    };
    return configs?.[status] || configs?.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleRowClick = (rotationId) => {
    navigate(`/rotation-detail?id=${rotationId}`);
  };

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-1">
            Recent Rotations
          </h2>
          <p className="text-sm md:text-base font-caption text-muted-foreground">
            Your latest flight activity and pay status
          </p>
        </div>
        
        <Button
          variant="outline"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => navigate('/rotations-list')}
        >
          View All
        </Button>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-accent-cyan/20">
              <th className="text-left py-4 px-4 text-sm font-caption text-muted-foreground">
                Rotation #
              </th>
              <th className="text-left py-4 px-4 text-sm font-caption text-muted-foreground">
                Date Range
              </th>
              <th className="text-left py-4 px-4 text-sm font-caption text-muted-foreground">
                Credit Hours
              </th>
              <th className="text-left py-4 px-4 text-sm font-caption text-muted-foreground">
                Block Hours
              </th>
              <th className="text-left py-4 px-4 text-sm font-caption text-muted-foreground">
                Status
              </th>
              <th className="text-right py-4 px-4 text-sm font-caption text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rotations?.map((rotation) => {
              const statusConfig = getStatusConfig(rotation?.status);
              return (
                <tr
                  key={rotation?.id}
                  className="border-b border-accent-cyan/10 hover:bg-secondary/50 transition-smooth cursor-pointer"
                  onClick={() => handleRowClick(rotation?.id)}
                >
                  <td className="py-4 px-4">
                    <span className="text-sm md:text-base font-data text-accent-cyan">
                      {rotation?.rotationNumber}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm md:text-base text-foreground">
                      {formatDate(rotation?.startDate)} - {formatDate(rotation?.endDate)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm md:text-base font-data text-foreground">
                      {rotation?.creditHours?.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm md:text-base font-data text-foreground">
                      {rotation?.blockHours?.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border ${statusConfig?.bgClass} ${statusConfig?.borderClass} ${statusConfig?.pulse ? 'animate-pulse' : ''}`}>
                      <Icon 
                        name={statusConfig?.icon} 
                        size={14}
                        className={statusConfig?.textClass}
                      />
                      <span className={`text-xs font-medium ${statusConfig?.textClass}`}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ChevronRight"
                      iconPosition="right"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleRowClick(rotation?.id);
                      }}
                    >
                      Details
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {rotations?.map((rotation) => {
          const statusConfig = getStatusConfig(rotation?.status);
          return (
            <div
              key={rotation?.id}
              className="p-4 rounded-xl bg-secondary border border-accent-cyan/20 hover:border-accent-cyan/40 transition-smooth cursor-pointer"
              onClick={() => handleRowClick(rotation?.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Rotation Number
                  </p>
                  <p className="text-lg font-data text-accent-cyan">
                    {rotation?.rotationNumber}
                  </p>
                </div>
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border ${statusConfig?.bgClass} ${statusConfig?.borderClass} ${statusConfig?.pulse ? 'animate-pulse' : ''}`}>
                  <Icon 
                    name={statusConfig?.icon} 
                    size={14}
                    className={statusConfig?.textClass}
                  />
                  <span className={`text-xs font-medium ${statusConfig?.textClass}`}>
                    {statusConfig?.label}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Date Range
                  </p>
                  <p className="text-sm text-foreground">
                    {formatDate(rotation?.startDate)}
                  </p>
                  <p className="text-sm text-foreground">
                    {formatDate(rotation?.endDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Hours
                  </p>
                  <p className="text-sm font-data text-foreground">
                    Credit: {rotation?.creditHours?.toFixed(2)}
                  </p>
                  <p className="text-sm font-data text-foreground">
                    Block: {rotation?.blockHours?.toFixed(2)}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="ChevronRight"
                iconPosition="right"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleRowClick(rotation?.id);
                }}
              >
                View Details
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentRotationsTable;